/**
 * @fileoverview Enhanced font size management hook and context provider
 * Combines responsive text sizing and font size control in a single hook
 */

import { createContext, useContext, useEffect, useState } from "react";
import { BREAKPOINTS, FONT_SIZES, FONT_SIZE_CONTROLS } from "@/lib/constants";

type Theme = "dark" | "light" | "autumn" | "winter" | "minimal";
type ScreenSize = keyof typeof BREAKPOINTS;

type FontSizeContextType = {
  fontSize: number;
  screenSize: ScreenSize;
  increaseFontSize: () => void;
  decreaseFontSize: () => void;
  resetFontSize: () => void;
};

const FontSizeContext = createContext<FontSizeContextType | undefined>(undefined);

function getInitialScreenSize(): ScreenSize {
  if (typeof window === 'undefined') return 'mobile';
  const width = window.innerWidth;

  if (width <= BREAKPOINTS.iPhoneSE) return 'iPhoneSE';
  if (width <= BREAKPOINTS.iPhonePro) return 'iPhonePro';
  if (width <= BREAKPOINTS.mobile) return 'mobile';
  if (width <= BREAKPOINTS.tablet) return 'tablet';
  return 'desktop';
}

export function FontSizeProvider({ children }: { children: React.ReactNode }) {
  const [screenSize, setScreenSize] = useState<ScreenSize>(getInitialScreenSize());
  const [fontSize, setFontSize] = useState(() => {
    try {
      const saved = localStorage.getItem("font-size");
      if (saved) {
        const parsedSize = Number(saved);
        return Math.min(
          Math.max(
            parsedSize,
            FONT_SIZE_CONTROLS.MIN[screenSize as keyof typeof FONT_SIZE_CONTROLS.MIN] || FONT_SIZE_CONTROLS.MIN.mobile
          ),
          FONT_SIZE_CONTROLS.MAX[screenSize as keyof typeof FONT_SIZE_CONTROLS.MAX] || FONT_SIZE_CONTROLS.MAX.mobile
        );
      }
      return FONT_SIZES[screenSize as keyof typeof FONT_SIZES] || FONT_SIZES.mobile;
    } catch {
      return FONT_SIZES[screenSize as keyof typeof FONT_SIZES] || FONT_SIZES.mobile;
    }
  });

  // Handle screen size changes
  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      let newSize: ScreenSize = 'mobile';

      if (width <= BREAKPOINTS.iPhoneSE) newSize = 'iPhoneSE';
      else if (width <= BREAKPOINTS.iPhonePro) newSize = 'iPhonePro';
      else if (width <= BREAKPOINTS.mobile) newSize = 'mobile';
      else if (width <= BREAKPOINTS.tablet) newSize = 'tablet';
      else newSize = 'desktop';

      setScreenSize(newSize);
    };

    const debouncedUpdate = () => {
      let timeoutId: number;
      return () => {
        clearTimeout(timeoutId);
        timeoutId = window.setTimeout(updateScreenSize, 150);
      };
    };

    const handleResize = debouncedUpdate();
    window.addEventListener('resize', handleResize);
    updateScreenSize();

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Adjust font size when screen size changes
  useEffect(() => {
    setFontSize(prev => {
      const minSize = FONT_SIZE_CONTROLS.MIN[screenSize as keyof typeof FONT_SIZE_CONTROLS.MIN] || FONT_SIZE_CONTROLS.MIN.mobile;
      const maxSize = FONT_SIZE_CONTROLS.MAX[screenSize as keyof typeof FONT_SIZE_CONTROLS.MAX] || FONT_SIZE_CONTROLS.MAX.mobile;
      return Math.min(Math.max(prev, minSize), maxSize);
    });
  }, [screenSize]);

  // Persist font size changes
  useEffect(() => {
    try {
      localStorage.setItem("font-size", fontSize.toString());
    } catch {
      // Ignore localStorage errors
    }
  }, [fontSize]);

  const increaseFontSize = () => {
    setFontSize(prev => {
      const step = FONT_SIZE_CONTROLS.STEP[screenSize as keyof typeof FONT_SIZE_CONTROLS.STEP] || FONT_SIZE_CONTROLS.STEP.mobile;
      const maxSize = FONT_SIZE_CONTROLS.MAX[screenSize as keyof typeof FONT_SIZE_CONTROLS.MAX] || FONT_SIZE_CONTROLS.MAX.mobile;
      return Math.min(prev + step, maxSize);
    });
  };

  const decreaseFontSize = () => {
    setFontSize(prev => {
      const step = FONT_SIZE_CONTROLS.STEP[screenSize as keyof typeof FONT_SIZE_CONTROLS.STEP] || FONT_SIZE_CONTROLS.STEP.mobile;
      const minSize = FONT_SIZE_CONTROLS.MIN[screenSize as keyof typeof FONT_SIZE_CONTROLS.MIN] || FONT_SIZE_CONTROLS.MIN.mobile;
      return Math.max(prev - step, minSize);
    });
  };

  const resetFontSize = () => {
    // Use FONT_SIZES for default values as they are properly mapped for each screen size
    setFontSize(FONT_SIZES[screenSize as keyof typeof FONT_SIZES] || FONT_SIZES.mobile);
  };

  return (
    <FontSizeContext.Provider value={{
      fontSize,
      screenSize,
      increaseFontSize,
      decreaseFontSize,
      resetFontSize,
    }}>
      {children}
    </FontSizeContext.Provider>
  );
}

export function useFontSize() {
  const context = useContext(FontSizeContext);
  if (!context) {
    throw new Error("useFontSize must be used within a FontSizeProvider");
  }
  return context;
}